import { gelNumberOf } from '@/utils/money';
import React from 'react';
import type { SupplierPaymentMethod } from '@/api/models';
import { parseGelToTetri } from '@/utils/money';
import { useResetOnKeyChange } from '@/utils';

/** One of the supplier's received invoices, offered as an allocation target. */
export type SupplierPaymentInvoiceOption = {
    id: string;
    invoiceNumber: string;
    /** Still owed on this invoice, in integer tetri (BOS-162). */
    outstandingTetri: number;
};

/**
 * What the modal hands back. `amount` is decimal GEL because that is the wire
 * shape; it is derived from the tetri the input parsed to, so the app's money
 * boundary is still crossed exactly once (`parseGelToTetri`).
 */
export type SupplierPaymentSubmit = {
    date: string;
    amount: number;
    method: SupplierPaymentMethod;
    reference: string | null;
    note: string | null;
    purchaseInvoiceId: string | null;
};

type UseSupplierPaymentFormParams = {
    isOpen: boolean;
    /** Today, ISO `YYYY-MM-DD` — the default date and the latest one allowed. */
    today: string;
    onSubmit: (input: SupplierPaymentSubmit) => void;
};

const nullIfBlank = (value: string): string | null => {
    const trimmed = value.trim();
    return trimmed.length === 0 ? null : trimmed;
};

/**
 * Why the amount is not acceptable, or null when it is. Mirrors the server's
 * rules (positive, at most two decimals) so the common rejections never cost a
 * round trip — the server still re-validates and stays the source of truth.
 */
export type SupplierPaymentAmountError =
    'invalid' | 'subTetri' | 'notPositive' | null;

export const validateAmount = (input: string): SupplierPaymentAmountError => {
    const parsed = parseGelToTetri(input);
    if (!parsed.ok) {
        switch (parsed.reason) {
            case 'empty':
                return null;
            case 'sub-tetri':
                return 'subTetri';
            case 'negative':
                return 'notPositive';
            default:
                return 'invalid';
        }
    }
    return parsed.tetri > 0 ? null : 'notPositive';
};

/**
 * Form state for the Record Payment modal. Resets on close rather than on open
 * so the fields do not visibly clear while the dialog animates away.
 */
export const useSupplierPaymentForm = ({
    isOpen,
    today,
    onSubmit,
}: UseSupplierPaymentFormParams) => {
    const [date, setDate] = React.useState(today);
    const [amount, setAmount] = React.useState('');
    const [method, setMethod] =
        React.useState<SupplierPaymentMethod>('BankTransfer');
    const [reference, setReference] = React.useState('');
    const [note, setNote] = React.useState('');
    const [invoiceId, setInvoiceId] = React.useState<string | null>(null);
    const [amountTouched, setAmountTouched] = React.useState(false);

    // Reset only on the open/close transition, matching useSupplierForm.
    useResetOnKeyChange(isOpen, () => {
        if (!isOpen) {
            setDate(today);
            setAmount('');
            setMethod('BankTransfer');
            setReference('');
            setNote('');
            setInvoiceId(null);
            setAmountTouched(false);
        }
    });

    const onDateChange = React.useCallback((value: string) => {
        setDate(value);
    }, []);

    const onAmountChange = React.useCallback((value: string) => {
        setAmount(value);
        setAmountTouched(true);
    }, []);

    const onAmountBlur = React.useCallback(() => {
        setAmountTouched(true);
    }, []);

    const onMethodChange = React.useCallback((value: SupplierPaymentMethod) => {
        setMethod(value);
    }, []);

    const onReferenceChange = React.useCallback((value: string) => {
        setReference(value);
    }, []);

    const onNoteChange = React.useCallback((value: string) => {
        setNote(value);
    }, []);

    const onInvoiceChange = React.useCallback((value: string | null) => {
        setInvoiceId(value);
    }, []);

    const parsed = parseGelToTetri(amount);
    const amountTetri = parsed.ok ? parsed.tetri : 0;
    const amountError = validateAmount(amount);
    // Surface the inline error only once the user has typed — a fresh, empty
    // field is not yet wrong.
    const showAmountError = amountTouched && amountError !== null;
    const isDateValid = date !== '' && date <= today;
    const isValid = amountTetri > 0 && amountError === null && isDateValid;

    // The modal deliberately does not close itself — the page hook closes on
    // success and keeps it open on failure so the inline error is visible and
    // nothing has to be re-entered.
    const onSubmitClick = React.useCallback(() => {
        if (!isValid) {
            return;
        }
        onSubmit({
            date,
            amount: gelNumberOf(amountTetri),
            method,
            reference: nullIfBlank(reference),
            note: nullIfBlank(note),
            purchaseInvoiceId: invoiceId,
        });
    }, [
        amountTetri,
        date,
        invoiceId,
        isValid,
        method,
        note,
        onSubmit,
        reference,
    ]);

    return {
        amount,
        amountError,
        date,
        invoiceId,
        isDateValid,
        isValid,
        method,
        note,
        reference,
        showAmountError,
        onAmountBlur,
        onAmountChange,
        onDateChange,
        onInvoiceChange,
        onMethodChange,
        onNoteChange,
        onReferenceChange,
        onSubmit: onSubmitClick,
    };
};
